/**
 * The overview's time window: its vocabulary, its URL shape, and the rules that
 * turn one into a pair of concrete instants.
 *
 * The endpoint accepts only explicit `start_time`/`end_time`
 * (`airapi/util/dashboard_window.py`); presets are entirely a frontend concern,
 * which is why they live in a pure module the page renders on top of rather
 * than inline in a component.
 *
 * Five rules are load-bearing:
 *
 * * **A preset is stored as a token, never as resolved timestamps.** A link to
 *   "7d" must show the recipient *their* last seven days. Only a custom range —
 *   deliberately chosen — is frozen into the URL as literal dates.
 * * **The window is half-open** — `[start, end)`. The server rejects
 *   `start >= end` and any span wider than {@link MAX_WINDOW_DAYS}. So an end
 *   bound is always the *next* midnight, never `23:59:59.999`: a day-inclusive
 *   window's exclusive end is the start of the day after it.
 * * **Day bounds are the viewer's local midnight, sent as an instant.** Mirrors
 *   Search's quick-select (`features/search/.../commonly-used.tsx`), which is
 *   the repo's precedent for exactly this: `startOfDay` in local time, through
 *   `toUTCISOString` for the wire. That sends UTC, as the backend requires,
 *   while "Aug 2" still means the viewer's Aug 2 — the alternative,
 *   re-stamping the calendar date as UTC midnight, would open the window at 7pm
 *   the previous evening for a Central reader while the caption claimed Aug 2.
 * * **`24h` is deliberately NOT day-snapped.** It is the page's only "right
 *   now" window, and there is no day-aligned reading of it that is still 24
 *   hours. Search draws the same line: its `Today` snaps, its `Last 24 hours`
 *   does not. Its `now` is floored to the minute — resolution runs on every
 *   render, and an unfloored `now` mints a fresh query key each time, so a
 *   refetch loop that looks like a flickering page. The snapped presets get
 *   that stability from their day boundaries for free.
 * * **A snapped window runs to the end of today**, which is in the future. That
 *   is Search's behaviour too, and it is what makes "7d" mean seven whole days
 *   rather than six-and-a-bit. Nothing can have happened in the remainder, so
 *   the figures are unaffected; the caption renders the last *included* day so
 *   the screen never names a day the window has not reached.
 */
import { addDays, startOfDay, subDays } from 'date-fns'

import { isMockingEnabled } from '@/lib/constants'

import { toUTCISOString } from '@/shared/datetime/utils/format'
import { time } from '@/shared/datetime/utils/time'

/**
 * Mirrors `MAX_WINDOW_DAYS` in `airapi/util/dashboard_window.py`. The server
 * states this limit in its rejection rather than clamping, so the picker
 * enforces it up front and the user never submits a request that can only fail.
 */
export const MAX_WINDOW_DAYS = 365

/**
 * The page's own period vocabulary, deliberately local to this subfeature.
 *
 * The shared `Period` enum belongs to the deprecated widget system, has no
 * 24-hour or 30-day member, and is still consumed by Graylog — extending it
 * would make two Platforms disagree about what a period is.
 *
 * `30d` rather than the design's "1 month" so the button cannot contradict the
 * window the backend actually resolves.
 */
export const OVERVIEW_PRESETS = ['24h', '7d', '30d'] as const

export type OverviewPreset = (typeof OVERVIEW_PRESETS)[number]

/** Narrows a bare string from a UI primitive to the preset vocabulary. */
export function isOverviewPreset(value: unknown): value is OverviewPreset {
	return OVERVIEW_PRESETS.includes(value as OverviewPreset)
}

/** The preset tokens plus the escape hatch for a hand-picked range. */
export type OverviewWindowToken = OverviewPreset | 'custom'

export const DEFAULT_PRESET: OverviewPreset = '7d'

/**
 * What each preset covers, and what its button says.
 *
 * `days` is a count of **whole calendar days ending with today**, so `7d` opens
 * at midnight six days ago — seven days including today, not eight. The label
 * has to be able to survive being read literally, which is the same reason the
 * third preset is `30d` rather than the design's "1 month".
 *
 * `24h` carries no day count: it is the rolling window (see the module doc).
 */
export const PRESET_SPANS: Record<
	OverviewPreset,
	{ days: number | null; label: string; caption: string }
> = {
	'24h': { days: null, label: '24h', caption: 'last 24 hours' },
	'7d': { days: 7, label: '7d', caption: 'last 7 days' },
	'30d': { days: 30, label: '30d', caption: 'last 30 days' },
}

/**
 * The exclusive upper bound of a day-snapped window ending today: the next
 * local midnight. The window is half-open, so "through the end of today" is
 * spelled as "up to the start of tomorrow".
 */
function exclusiveEndOfToday(now: Date): Date {
	return startOfDay(addDays(now, 1))
}

/**
 * The window as it lives in the URL.
 *
 * `from`/`to` are `yyyy-MM-dd` calendar dates and are meaningful only when
 * `preset` is `custom`; they are the one case where the URL is literal.
 *
 * `preset` is **optional**, and its absence is load-bearing rather than
 * shorthand for {@link DEFAULT_PRESET}: it is what distinguishes a bare
 * `/air/overview` — which the route is free to answer with the viewer's
 * remembered preset — from a deliberate `?preset=7d`, which it must not
 * override. So the fallback is applied here, at read time, and never written
 * into the schema as a parse-time default. See `window-preference.ts`.
 */
export type OverviewWindowSearch = {
	preset?: OverviewWindowToken
	from?: string
	to?: string
}

export type ResolvedWindow = {
	/** ISO-8601 UTC, inclusive. */
	start: string
	/** ISO-8601 UTC, exclusive. */
	end: string
}

/** `now` with seconds and milliseconds discarded — the query-key stability rule. */
export function flooredNow(now: Date = new Date()): Date {
	const floored = new Date(now)
	floored.setSeconds(0, 0)
	return floored
}

/**
 * Turn the URL's window into the two instants the endpoint takes.
 *
 * Only an **unreadable** custom range falls back to the default preset: search
 * params are user-editable, and a mistyped date leaves nothing to request. A
 * range that is merely *wrong* — inverted, or wider than the endpoint allows —
 * is sent as asked and left to the server, whose rejection carries a sentence
 * written for the user and lands inline on the filter bar. Silently substituting
 * a different window would answer a question nobody asked.
 *
 * `now` is injectable so a test can assert the boundary arithmetic without
 * racing the clock.
 */
export function resolveWindow(
	search: OverviewWindowSearch,
	now: Date = new Date(),
): ResolvedWindow {
	if (search.preset === 'custom') {
		return (
			resolveCustomRange(search.from, search.to) ??
			presetWindow(DEFAULT_PRESET, now)
		)
	}

	return presetWindow(search.preset ?? DEFAULT_PRESET, now)
}

/**
 * A preset's two instants.
 *
 * The snapped presets run from midnight `days - 1` days ago to the next
 * midnight — whole days, today included. `24h` is the rolling exception and is
 * the only one that needs `now` floored to the minute for a stable query key.
 */
function presetWindow(preset: OverviewPreset, now: Date): ResolvedWindow {
	const { days } = PRESET_SPANS[preset]

	if (days === null) {
		const end = flooredNow(now)
		return {
			start: toUTCISOString(new Date(end.getTime() - time(24, 'h'))),
			end: toUTCISOString(end),
		}
	}

	return {
		start: toUTCISOString(startOfDay(subDays(now, days - 1))),
		end: toUTCISOString(exclusiveEndOfToday(now)),
	}
}

/**
 * A custom `yyyy-MM-dd` pair as instants, or `null` when either date is missing
 * or unreadable.
 *
 * Both bounds are whole local days: `from` opens at its midnight and `to` closes
 * at the *next* one, so "Aug 1 to Aug 5" covers all five days rather than four
 * and a bit. A single-day pick (`from === to`) is a legitimate one-day window,
 * not an empty one.
 */
function resolveCustomRange(
	from: string | undefined,
	to: string | undefined,
): ResolvedWindow | null {
	const fromDate = parseCalendarDate(from)
	const toDate = parseCalendarDate(to)
	if (!fromDate || !toDate) return null

	return {
		start: toUTCISOString(startOfDay(fromDate)),
		end: toUTCISOString(startOfDay(addDays(toDate, 1))),
	}
}

/**
 * Read a `yyyy-MM-dd` search param as a local calendar date.
 *
 * Parsed field by field rather than through `new Date(string)`, which reads a
 * bare `yyyy-MM-dd` as **UTC midnight** — a day earlier than the user picked
 * for anyone west of Greenwich.
 */
export function parseCalendarDate(value: string | undefined): Date | null {
	if (!value) return null
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
	if (!match) return null

	const [, year, month, day] = match
	const date = new Date(Number(year), Number(month) - 1, Number(day))
	// Rejects the impossible dates the regex admits (`2026-02-31` rolls over).
	if (date.getMonth() !== Number(month) - 1) return null
	return Number.isNaN(date.getTime()) ? null : date
}

/** A local `Date` as the `yyyy-MM-dd` the URL carries. Never UTC — see above. */
export function toCalendarDate(date: Date): string {
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')
	return `${date.getFullYear()}-${month}-${day}`
}

/**
 * The period an executive report covers: the last COMPLETE calendar month.
 *
 * Not a preset — the page's filter cannot select it and no URL carries it. It
 * lives here because it is window arithmetic and answers to the same two rules
 * as everything above: half-open, so it opens at the first of last month and
 * closes at the first of this one ("March" is all of March and none of April);
 * and local midnight sent as an instant, so "March" means the *viewer's* March
 * rather than a month that starts at 7pm in February.
 *
 * Stable for a whole month, so it is a stable query key across renders.
 */
export function previousCalendarMonth(now: Date = new Date()): ResolvedWindow {
	return {
		start: toUTCISOString(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
		end: toUTCISOString(new Date(now.getFullYear(), now.getMonth(), 1)),
	}
}

/**
 * How far back the mock world's oldest case sits.
 *
 * The padded backlog is aged `-2d`..`-30d`, and a second batch fills the
 * previous calendar month for the demo's Executive Report. That month opens at
 * most 61 days before today (the 31st of a month whose predecessor has 31
 * days), so 62 covers all of it. Mirrors the search window's identical
 * accommodation (`features/search/components/time-range/start-end-date.tsx`):
 * both anchor to the same load-time `now` as the seed does, so the intersection
 * never drifts.
 */
export const MOCK_EARLIEST_CASE_DAYS = 62

/**
 * The furthest-back date a custom range may open on.
 *
 * The picker disables everything earlier, which is what keeps an over-long
 * window from being submittable at all rather than merely refused. Counted so
 * that selecting this date *through today* is exactly {@link MAX_WINDOW_DAYS}
 * whole days — the widest span the endpoint accepts.
 *
 * Under mocking the floor is the world's oldest case instead, so a prospect
 * exploring the picker cannot land on a range that predates the seeded data and
 * dead-end on an empty state — the one thing the demo must never show.
 */
export function earliestSelectableDate(today: Date = new Date()): Date {
	const days = isMockingEnabled ? MOCK_EARLIEST_CASE_DAYS : MAX_WINDOW_DAYS
	return startOfDay(subDays(today, days - 1))
}

/**
 * The next-wider window to offer from the page's empty state.
 *
 * Widening walks up the preset ladder; from its top rung — and from any custom
 * range narrower than it — the only genuinely wider window left is the widest
 * the endpoint permits, so that is what the last step offers. Returns `null`
 * only when the current window already is that, which the picker's own limit
 * makes unreachable from the UI.
 */
export function widenTarget(
	search: OverviewWindowSearch,
	now: Date = new Date(),
): OverviewWindowSearch | null {
	const preset = search.preset ?? DEFAULT_PRESET
	if (preset === '24h') return { preset: '7d' }
	if (preset === '7d') return { preset: '30d' }

	const resolved = resolveWindow(search, now)
	const span = Date.parse(resolved.end) - Date.parse(resolved.start)
	if (span >= time(MAX_WINDOW_DAYS, 'd')) return null

	return {
		preset: 'custom',
		from: toCalendarDate(earliestSelectableDate(now)),
		to: toCalendarDate(now),
	}
}

/**
 * What the widen button says, so the empty state names the window it offers
 * rather than promising a period it will not actually load — the custom step's
 * reach is whatever the picker's own floor allows, which mocking lowers.
 */
export function widenLabel(target: OverviewWindowSearch): string {
	if (target.preset !== 'custom') {
		return `Widen to the ${PRESET_SPANS[target.preset ?? DEFAULT_PRESET].caption}`
	}

	const from = parseCalendarDate(target.from)
	const to = parseCalendarDate(target.to)
	if (!from || !to) return 'Widen the time range'

	// Rounded, not truncated: a DST boundary inside the range makes the span an
	// hour short of a whole number of days, which would otherwise read as one
	// day fewer than the picker will actually select.
	const days = Math.round(
		(startOfDay(addDays(to, 1)).getTime() - startOfDay(from).getTime()) /
			time(1, 'd'),
	)
	return `Widen to the last ${days} days`
}
